import {FC, useEffect, useState} from 'react';

import {useTranslation} from 'react-i18next';

import useAnalytics, {
  ANALYTICS,
  BASKET_EDIT_SOURCE_EVENTS,
  SUGGESTEDLIST_CHANNELS,
  EVENT_SOURCE
} from 'contexts/analytics-context';
import {useAppDispatch, useAppSelector} from 'hooks';
import ReactNative from 'services/react-native';
import SendBrowserEvent from 'services/send-browser-event';
import {setSelectedAddressAction} from 'store/actions/addresses-action';
import {setBasketQuantityChangedProductMapAction, setIsLoadingAction} from 'store/actions/basket-action';
import {UserAddress} from 'types';
import {getCheckoutLatLng, isCheckoutV3BasedStore, isCheckoutV3Enable} from 'utils';
import {filter1PItems, filter3PItems, isSyncAddressEnabled} from 'utils/helper';
import {checkIfAddressLocationIsDeliverable} from 'utils/location';
import AddressDrawerFooter from 'views/address-drawer-footer/AddressDrawerFooter';

import AddressDrawerBody from './AddressDrawerBody';
import AddressDrawerHeader from './AddressDrawerHeader';
import {StyledAddressDrawer} from './styles';

export type AddressDrawerProps = {
  title: string;
  open: boolean;
  toggleAddressDrawer: () => void;
  onEditAddress?: (address: UserAddress) => void;
  shippingAddress: UserAddress | undefined;
  onNewAddress: () => void;
  updateStoresForBasket: (address: UserAddress) => Promise<void>;
};

export const AddressDrawerView: FC<AddressDrawerProps> = ({
  title,
  open,
  toggleAddressDrawer,
  onEditAddress,
  shippingAddress,
  onNewAddress,
  updateStoresForBasket
}) => {
  const {t} = useTranslation();
  const analytics = useAnalytics();
  const dispatch = useAppDispatch();
  const {isDirectCheckout, currentStore, locations} = useAppSelector((state) => state?.app);
  const userAddressList = useAppSelector((state) => state?.addresses?.availableAddresses) || [];
  const basket = useAppSelector((state) => state?.basket?.basket);
  const isReactNativeView = ReactNative.isReactNativeWebView();

  const isSyncAddress = isSyncAddressEnabled();
  const [loading, setLoading] = useState<boolean>(false);
  const [userClickedAddress, setUserClickedAddress] = useState<UserAddress | undefined>(shippingAddress);

  const isCheckoutV3Store = isCheckoutV3BasedStore(currentStore?.storeCodes[0] as string);
  const isCheckoutV3 = isCheckoutV3Enable() && isCheckoutV3Store;

  useEffect(() => {
    setUserClickedAddress(shippingAddress);
  }, [shippingAddress]);

  const handleFooterButtonClick = () => {
    if (userClickedAddress) {
      handleShippingAddressChange(userClickedAddress);
    }
  };

  const arrayToSort = [...userAddressList];
  const sortedAddresses = arrayToSort?.sort((a) => (a?.id === shippingAddress?.id ? -1 : 1));

  const handleShippingAddressChange = async (address: UserAddress) => {
    setLoading(true);
    try {
      if (!address?.user_latitude || !address?.user_longitude) {
        onEditAddress && onEditAddress(address);
        return;
      } else if (
        address.zip !== shippingAddress?.zip ||
        address.subDistrict?.nameEn !== shippingAddress?.subDistrict?.nameEn ||
        address.id !== shippingAddress?.id
      ) {
        await updateStoresForBasket(address);
        SendBrowserEvent.updateZipAndSubdistricts(address);
        if (isSyncAddress) {
          SendBrowserEvent.updateMetaDataZipAndSubdistricts(address);
        }
        dispatch(setBasketQuantityChangedProductMapAction({}));
      }
      if (isDirectCheckout) {
        if (!address?.user_latitude || !address?.user_longitude) {
          onEditAddress && onEditAddress(address);
          SendBrowserEvent.showInfoToast(t('addressV2.confirmLocation'));
          return;
        }

        if (checkIfAddressLocationIsDeliverable(locations, shippingAddress as UserAddress)) {
          SendBrowserEvent.showErrorToast(undefined, t('message.undeliverableAddressLocation'));
          return;
        }

        const {latitude, longitude} = getCheckoutLatLng(address);
        const userAddress = {...address, latitude, longitude};

        SendBrowserEvent.handleDirectCheckout(userAddress, isCheckoutV3);
        dispatch(setIsLoadingAction(true));
      }
      !isSyncAddress && localStorage.setItem('selectedShippingAddressId', address?.id || '');
      dispatch(setSelectedAddressAction(address as UserAddress));
      toggleAddressDrawer();
      addBasketEditAnalytics(shippingAddress as UserAddress);
    } catch (error) {
      console.log('error:', error);
    } finally {
      setLoading(false);
    }
  };

  const addBasketEditAnalytics = async (address: UserAddress) => {
    analytics.trackEvent(ANALYTICS.EDIT_BASKET, {
      sourceEvent: BASKET_EDIT_SOURCE_EVENTS.SWITCH_ADDRESS,
      basketId: basket?.id || '',
      addressId: address?.id || '',
      itemsCount: basket?.basketItems?.length || '',
      productIds: basket?.basketItems?.map((item: any) => item.productId) || [],
      basketItems: basket?.basketItems?.map((item: any) => item.productId) || [],
      priceBeforeDiscount: basket?.prices?.totalOriginalPrice || '',
      total: basket?.prices?.totalFinalPriceToPay || '',
      delivery: basket?.prices?.shippingFee || '',
      discount: basket?.prices?.roundedDiscountAmount || '',
      storeId: basket?.storeCode || '',
      channel: SUGGESTEDLIST_CHANNELS.MAKRO_PRO,
      segment_source_name: isReactNativeView ? EVENT_SOURCE.APP : EVENT_SOURCE.WEB,
      basketSize: basket?.basketItems?.length || 0,
      onePCount: filter1PItems(basket?.basketItems || []).length || 0,
      threePCount: filter3PItems(basket?.basketItems || []).length || 0
    });
  };

  useEffect(() => {
    const zip = shippingAddress?.zip || '';
    const sub = shippingAddress?.subDistrict?.nameEn || '';
    if (open) {
      analytics.trackEvent(ANALYTICS.ADDRESS_LIST_OPEN, {
        addressesTotal: userAddressList.length,
        zip,
        sub,
        storeId: basket?.storeCode || '',
        channel: SUGGESTEDLIST_CHANNELS.MAKRO_PRO,
        segment_source_name: isReactNativeView ? EVENT_SOURCE.APP : EVENT_SOURCE.WEB,
        basketSize: basket?.basketItems?.length || 0,
        onePCount: filter1PItems(basket?.basketItems || []).length || 0,
        threePCount: filter3PItems(basket?.basketItems || []).length || 0
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <StyledAddressDrawer variant="temporary" anchor="right" open={open} onClose={toggleAddressDrawer}>
      <AddressDrawerHeader title={title} onBack={toggleAddressDrawer} />
      <AddressDrawerBody
        onEditAddress={onEditAddress}
        sortedAddresses={sortedAddresses || []}
        onAddressClick={(address) => {
          setUserClickedAddress(address);
        }}
        selectedShippingAddress={shippingAddress}
        onNewAddress={onNewAddress}
      />
      <AddressDrawerFooter
        isDisabled={!userClickedAddress}
        loading={loading}
        onClick={handleFooterButtonClick}
        actionButtonLabel={t('button.Confirm')}
      />
    </StyledAddressDrawer>
  );
};

AddressDrawerView.displayName = 'AddressDrawerView';
